import React, { useEffect, useState } from "react";
import {
  useGetSchedulersQuery,
  useDeleteScheduleMutation,
  useUpdateSchedulerStatusMutation,
  ScheduleType,
} from "../store/slices/settingsSlice";
import MyPagination from "../Common/MyPagination";
import ScheduleSettings from "./ScheduleSettings";
import { toast } from "react-toastify";
import ScheduleEdit from "./ScheduleEdit";
import MyModal from "../Common/MyModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Dropdown, DropdownButton, Form, Table } from "react-bootstrap";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";
import { Link } from "react-router-dom";
import SearchIcon from "../../assets/icons/search.svg2";

const columnOptions = [
  { value: "frequency", label: "Frequency" },
  { value: "active", label: "Active" },
  { value: "startDate", label: "Start Date" },
  { value: "endDate", label: "End Date" },
  { value: "maxRuns", label: "Max Runs" },
  { value: "nextRun", label: "Next Run" },
];

export default function SchedulersList({
  broadcasterUuid,
  projectUuid,
  schedulerType,
  embedded = false,
}: {
  broadcasterUuid?: string;
  projectUuid?: string;
  schedulerType?: "project" | "broadcaster";
  embedded?: boolean;
}) {
  const confirmDialog = useConfirmDialog() as
    | ((heading: string, content: string) => Promise<boolean>)
    | null;
  const [pagination, setPagination] = useState({
    scoped: { page: 0, perPage: 10 },
    project: { page: 0, perPage: 10 },
    broadcaster: { page: 0, perPage: 10 },
  });
  const [schedulerFilters, setSchedulerFilters] = useState<{
    project_uuid?: string;
    broadcaster_uuid?: string;
    is_active?: boolean;
    search?: string;
  }>({ broadcaster_uuid: broadcasterUuid, project_uuid: projectUuid });
  const [inputText, setInputText] = useState("");
  const [columnsToShow, setColumnsToShow] = useState(
    columnOptions.map((column) => column.value)
  );
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSchedulerFilters((currentFilters) => ({
        ...currentFilters,
        search: inputText || undefined,
      }));
    }, 300);

    return () => clearTimeout(timeout);
  }, [inputText]);
  const hasScopedTarget = Boolean(projectUuid || broadcasterUuid);
  const scopedSchedulerType = projectUuid
    ? "project"
    : broadcasterUuid
    ? "broadcaster"
    : undefined;
  const updatePagination = (
    section: "scoped" | "project" | "broadcaster",
    page: number,
    perPage: number
  ) => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      [section]: { page, perPage },
    }));
  };
  const scopedSchedulerQueryParams = {
    page: pagination.scoped.page,
    per_page: pagination.scoped.perPage,
    ...schedulerFilters,
  };
  const projectSchedulerQueryParams = {
    page: pagination.project.page,
    per_page: pagination.project.perPage,
    ...schedulerFilters,
  };
  const broadcasterSchedulerQueryParams = {
    page: pagination.broadcaster.page,
    per_page: pagination.broadcaster.perPage,
    ...schedulerFilters,
  };

  const { data: scopedSchedulersData, refetch: refetchScopedSchedulers } =
    useGetSchedulersQuery(
      { ...scopedSchedulerQueryParams, type: scopedSchedulerType },
      { skip: !hasScopedTarget }
    );
  const { data: projectSchedulersData, refetch: refetchProjectSchedulers } =
    useGetSchedulersQuery(
      { ...projectSchedulerQueryParams, type: "project" },
      { skip: hasScopedTarget }
    );
  const { data: broadcastSchedulersData, refetch: refetchBroadcastSchedulers } =
    useGetSchedulersQuery(
      { ...broadcasterSchedulerQueryParams, type: "broadcaster" },
      { skip: hasScopedTarget }
    );
  const [deleteSchedule] = useDeleteScheduleMutation();
  const [updateSchedulerStatus] = useUpdateSchedulerStatusMutation();
  const [editedSchedule, setEditedSchedule] = useState<
    ScheduleType | undefined
  >(undefined);
  const getErrorMessage = (error: unknown) => {
    if (
      error &&
      typeof error === "object" &&
      "data" in error &&
      error.data &&
      typeof error.data === "object" &&
      "message" in error.data
    ) {
      return String(error.data.message);
    }

    return "Unknown error";
  };
  const confirm = async (heading: string, content: string) => {
    if (!confirmDialog) {
      return false;
    }

    return confirmDialog(heading, content);
  };
  const schedules = scopedSchedulersData?.schedules || [];
  const projectSchedulers = projectSchedulersData?.schedules || [];
  const broadcastSchedulers = broadcastSchedulersData?.schedules || [];
  const schedulerSections = hasScopedTarget
    ? [
        {
          title: projectUuid ? "Project Schedulers" : "Broadcast Schedulers",
          schedules,
          emptyMessage: "No schedulers found.",
          refetch: refetchScopedSchedulers,
          total: scopedSchedulersData?.total || 0,
          onPaginate: (page: number, perPage: number) =>
            updatePagination("scoped", page, perPage),
        },
      ]
    : [
        {
          title: embedded ? "Component Schedulers" : "Project Schedulers",
          schedules: projectSchedulers,
          emptyMessage: embedded
            ? "No component schedulers found."
            : "No project schedulers found.",
          refetch: refetchProjectSchedulers,
          total: projectSchedulersData?.total || 0,
          onPaginate: (page: number, perPage: number) =>
            updatePagination("project", page, perPage),
        },
        {
          title: "Broadcast Schedulers",
          schedules: broadcastSchedulers,
          emptyMessage: "No broadcast schedulers found.",
          refetch: refetchBroadcastSchedulers,
          total: broadcastSchedulersData?.total || 0,
          onPaginate: (page: number, perPage: number) =>
            updatePagination("broadcaster", page, perPage),
        },
      ].filter(
        (section) =>
          !schedulerType ||
          section.title ===
            `${schedulerType === "project" && embedded ? "Component" : schedulerType === "project" ? "Project" : "Broadcast"} Schedulers`
      );

  const renderSchedulersTable = (
    title: string,
    sectionSchedules: ScheduleType[],
    emptyMessage: string,
    refetchSchedules: () => void,
    total: number,
    onPaginate: (page: number, perPage: number) => void
  ) => (
    <div className="mb-1" key={title}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        {!embedded && <h3 className="h5 mb-0">{title}</h3>}
        <div className="d-flex ms-auto gap-3">
          <div className="position-relative">
            <input
              className="form-control"
              placeholder="Search"
              style={{ height: 38, paddingRight: 40 }}
              type="search"
              value={inputText}
              onChange={(event) => setInputText(event.target.value)}
            />
            <SearchIcon
              width={30}
              heigth={30}
              className="white bg-colored position-absolute top-50 end-0 translate-middle rounded-circle p-2"
              style={{ marginRight: -10 }}
            />
          </div>
          <DropdownButton
            id={`${title.replaceAll(" ", "-").toLowerCase()}-columns-dropdown`}
            title="Show columns"
            variant="colored"
            autoClose="outside"
            align="end"
          >
            {columnOptions.map((column) => (
              <Dropdown.Item as="div" key={column.value}>
                <Form.Check
                  checked={columnsToShow.includes(column.value)}
                  label={column.label}
                  id={`${title}-${column.value}-checkbox`}
                  onChange={() =>
                    setColumnsToShow((currentColumns) =>
                      currentColumns.includes(column.value)
                        ? currentColumns.filter((value) => value !== column.value)
                        : [...currentColumns, column.value]
                    )
                  }
                />
              </Dropdown.Item>
            ))}
          </DropdownButton>
          <button
            type="button"
            className="btn btn-sm border-0"
            onClick={refetchSchedules}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRotateLeft} />
          </button>
        </div>
      </div>
      <Table striped bordered hover>
          <thead>
            <tr>
              <th>Label</th>
              {!projectUuid && !broadcasterUuid ? <th>Target</th> : null}
              {columnsToShow.includes("frequency") && <th>Frequency</th>}
              {columnsToShow.includes("active") && <th>Active</th>}
              {columnsToShow.includes("startDate") && <th>Start Date</th>}
              {columnsToShow.includes("endDate") && <th>End Date</th>}
              {columnsToShow.includes("maxRuns") && <th>Max Runs</th>}
              {columnsToShow.includes("nextRun") && <th>Next Run</th>}
              <th className=""></th>
            </tr>
          </thead>
          <tbody>
            {sectionSchedules.map((scheduler) => (
              <tr key={scheduler.Uuid}>
                <td>
                  <MyModal
                    label={scheduler.Label}
                    heading="Edit Schedule"
                    size="lg"
                    buttonClassName="text-start btn-link"
                    show={editedSchedule?.Uuid === scheduler.Uuid}
                    onHide={() => {
                      setEditedSchedule(undefined);
                    }}
                  >
                    {({ handleClose }) => (
                      <ScheduleEdit
                        uuid={scheduler.Uuid}
                        broadcasterUuid={scheduler.BroadcasterUuid}
                        projectUuid={scheduler.ProjectUuid}
                        onClose={handleClose}
                        onSave={handleClose}
                      />
                    )}
                  </MyModal>
                </td>
                {!projectUuid && !broadcasterUuid && (
                  <td>
                    {scheduler.Broadcaster && !broadcasterUuid ? (
                      <>
                        <Link
                          to={`/library/broadcast/${scheduler.BroadcasterUuid}/messages`}
                          title={scheduler.Broadcaster.Description}
                        >
                          {scheduler.Broadcaster.Label}
                        </Link>
                        <span className="badge bg-secondary ms-2">
                          Broadcast
                        </span>
                      </>
                    ) : (
                      ""
                    )}
                    {scheduler.Project && !projectUuid ? (
                      <>
                        <Link
                          to={`/library/logic/builder/${scheduler.Project.ID}`}
                          title={scheduler.Project.Description}
                        >
                          {scheduler.Project.Label}
                        </Link>
                        <span className="badge bg-secondary ms-2">
                          Logic component
                        </span>
                      </>
                    ) : (
                      ""
                    )}
                  </td>
                )}
                {columnsToShow.includes("frequency") && <td>
                  {scheduler.MaxRuns === 1 ? (
                    <span className="badge bg-secondary text-capitalize">
                      One-time
                    </span>
                  ) : (
                    <>
                      <span className="badge bg-secondary text-capitalize">
                        {scheduler.FrequencyKind === "fixed"
                          ? "Daily"
                          : scheduler.FrequencyKind}
                      </span>
                      {scheduler.FrequencyKind === "interval" && (
                        <span className="ms-1 text-secondary small">
                          {scheduler.IntervalValue} {scheduler.IntervalUnit}
                        </span>
                      )}
                    </>
                  )}
                </td>}
                {columnsToShow.includes("active") && <td>
                  <Form.Check // prettier-ignore
                    type="switch"
                    id={`schedule-is-active-switch-${scheduler.Uuid}`}
                    checked={scheduler.IsActive}
                    onChange={async () => {
                      const result = await updateSchedulerStatus({
                        uuid: scheduler.Uuid || "",
                        isActive: !scheduler.IsActive,
                      });
                      if ("error" in result) {
                        console.log(result.error);

                        if (
                          !(await confirm(
                            "Could not update scheduler status",
                            `
                              We could not update the scheduler status due to
                              the following error:
                              <div class="alert alert-secondary my-2">
                                ${getErrorMessage(result.error)}
                              </div>
                               Would you like to edit the schedule?
                            `
                          ))
                        ) {
                          return;
                        }
                        setEditedSchedule(scheduler);
                      } else {
                        toast.success("Scheduler status updated successfully");
                      }
                    }}
                  />
                </td>}
                {columnsToShow.includes("startDate") && <td>{scheduler.StartDate?.slice(0, 10) || "—"}</td>}
                {columnsToShow.includes("endDate") && <td>{scheduler.EndDate?.slice(0, 10) || "—"}</td>}
                {columnsToShow.includes("maxRuns") && <td>
                  {!scheduler.MaxRuns
                    ? scheduler.EndDate
                      ? "Run until " + scheduler.EndDate?.slice(0, 10)
                      : "Run forever"
                    : scheduler.MaxRuns}
                </td>}
                {columnsToShow.includes("nextRun") && <td>
                  {scheduler.NextRun && scheduler.IsActive
                    ? (() => {
                        const nextRunDate = new Date(scheduler.NextRun);
                        if (isNaN(nextRunDate.getTime())) {
                          return "Invalid date";
                        }
                        return `${nextRunDate.getFullYear()}-${(
                          nextRunDate.getMonth() + 1
                        )
                          .toString()
                          .padStart(2, "0")}-${nextRunDate
                          .getDate()
                          .toString()
                          .padStart(2, "0")} ${nextRunDate
                          .getHours()
                          .toString()
                          .padStart(2, "0")}:${nextRunDate
                          .getMinutes()
                          .toString()
                          .padStart(2, "0")}`;
                      })()
                    : "—"}
                </td>}
                <td>
                  {scheduler.Uuid ? (
                    <button
                      className="btn btn-sm border-0 btn-outline-danger"
                      onClick={async () => {
                        if (
                          !(await confirm(
                            "Delete schedule?",
                            "Are you sure you want to delete this schedule? This action cannot be undone."
                          ))
                        ) {
                          return;
                        }
                        const result = await deleteSchedule({
                          uuid: scheduler.Uuid || "",
                        });
                        if ("error" in result) {
                          toast.error("Failed to delete schedule");
                        } else {
                          toast.success("Schedule deleted successfully");
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!sectionSchedules.length && (
              <tr>
                <td
                  className="text-center text-secondary"
                  colSpan={
                    2 +
                    (!projectUuid && !broadcasterUuid ? 1 : 0) +
                    columnsToShow.length
                  }
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
      </Table>
      <MyPagination
        total={total}
        onPaginate={onPaginate}
        perPageOptions={embedded ? [10, 25, 50] : undefined}
        defaultPerPage={embedded ? 10 : undefined}
        alwaysVisible={embedded}
      />
    </div>
  );

  const schedulerTables = schedulerSections.map(
    ({ title, schedules, emptyMessage, refetch, total, onPaginate }) =>
      renderSchedulersTable(
        title,
        schedules,
        emptyMessage,
        refetch,
        total,
        onPaginate
      )
  );

  return embedded ? (
    <>{schedulerTables}</>
  ) : (
    <div className="p-3 rounded-3 bg-white my-3">{schedulerTables}</div>
  );
}
